import React, { useEffect, useState } from "react";
import { View, StyleSheet, Platform } from "react-native";
import { useResponsive } from "../../../../hooks/useResponsive";
import variables from "../../../../styles/variables";
import { Tip as TipT, Tipster } from "../../../../types/tipster";
import StandardPriceButton from "../../../Buttons/StandardPriceButton";
import { getOdds } from "../../../../client/odds";
import { Odd } from "../../../../types/races/runner";
import { LoadingState } from "../../../States/states";
import TipInfo from "./shared/TipInfo";
import TipComment from "./shared/TipComment";

/**
 * Props for the Tip component.
 * @typedef {Object} TipProps
 * @property {TipT} [tip] - The tip data object.
 * @property {number} [i] - The index of the current tip.
 * @property {number} [tipCount] - The total number of tips.
 * @property {boolean} [skipLoader=false] - Whether to skip showing the loading state.
 * @property {boolean} [individualTipster=false] - Whether the tip is for an individual tipster.
 * @property {Tipster} [tipster] - The tipster associated with the tip.
 */

/**
 * The Tip component displays information about a single tip, including its details,
 * comments, and associated odds. It adjusts its layout based on screen size.
 *
 * @param {TipProps} props - The props for the Tip component.
 * @returns {JSX.Element} The rendered Tip component.
 */

const { colors, spacing } = variables;

type TipProps = {
  tip?: TipT;
  i?: number;
  tipCount?: number;
  skipLoader?: boolean;
  individualTipster?: boolean;
  tipster?: Tipster;
};

const Tip = ({
  tip,
  i,
  tipCount,
  skipLoader = false,
  individualTipster = false,
  tipster,
}: TipProps) => {
  const { isSmallAndUp } = useResponsive();
  const [bestOdds, setBestOdds] = useState<Odd | undefined>();
  const [contentLoading, setContentLoading] = useState<boolean>(true);
  const lastTip = tipCount == i + 1;

  useEffect(() => {
    setContentLoading(true);
    getOdds([tip.runner])
      .then(([[{ bestOdds }]]) => setBestOdds(bestOdds))
      .catch((e) => console.error("Error:", e.message))
      .finally(() => setContentLoading(false));
  }, [tip.runner]);

  if (contentLoading && !skipLoader)
    return <LoadingState name="TipsterLoader" />;

  if (contentLoading && skipLoader) return <LoadingState name="TipLoader" />;

  return (
    <View
      key={i}
      style={[
        (tip.result.state == "lost" || tip.result.state == "refunded") &&
        !tip.successful
          ? styles.opacity
          : null,
      ]}
    >
      {/* smaller screens */}
      {!isSmallAndUp && (
        <View style={[styles.details, lastTip ? {} : styles.separator]}>
          <View
            style={{
              flexDirection: "row",
              marginVertical: spacing.xsmall,
              justifyContent: "space-between",
            }}
          >
            <TipInfo tip={tip} individualTipster={individualTipster} />
            <View
              style={{
                alignItems: "flex-end",
                justifyContent: "center",
              }}
            >
              <StandardPriceButton
                odd={bestOdds}
                race={tip?.runner?.race}
                runner={tip?.runner}
                startingPrice={tip?.starting_price}
              />
            </View>
          </View>
          <TipComment tip={tip} tipster={tipster} />
        </View>
      )}
      {/* bigger screens */}
      {isSmallAndUp && (
        <View
          style={[
            styles.details,
            { flexDirection: "row", marginTop: spacing.xsmall },
            lastTip ? {} : styles.separator,
          ]}
        >
          <View style={{ flex: 1 }}>
            <TipInfo tip={tip} individualTipster={individualTipster} />
          </View>
          <View style={{ flex: 2 }}>
            <TipComment tip={tip} tipster={tipster} />
          </View>
          <View
            style={{
              flex: 0.5,
              alignItems: "flex-end",
              justifyContent: "center",
            }}
          >
            <View style={{ alignItems: "flex-end", justifyContent: "center" }}>
              <StandardPriceButton
                startingPrice={tip?.starting_price}
                odd={bestOdds}
                race={tip?.runner?.race}
                runner={tip?.runner}
              />
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  opacity: {
    opacity: 0.4,
  },
  details: {
    paddingBottom: spacing.xsmall,
    paddingHorizontal: spacing.xxsmall,
  },
  separator: {
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderColor: colors.palette.neutral.x200,
  },
  textLink: {
    ...Platform.select({
      web: {},
      ios: {
        marginBottom: -3,
      },
      android: {
        marginBottom: -4,
      },
    }),
  },
  successfulTip: {
    padding: spacing.xxsmall,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: colors.palette.rtv.secondary,
    marginBottom: spacing.xxsmall,
  },
  expertEyeIcon: {
    marginLeft: spacing.xxsmall,
  },
});

export default Tip;
